import React, { memo } from "react";
import { geoCentroid } from "d3-geo";
import { scaleQuantize } from "d3-scale";
import { useTranslation } from "react-i18next";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { ReactComponent as IrelandMap } from "./ireland.svg";

const geoUrl = "/assets/data/ireland11.json";

const MapChartIRL = ({
  setTooltipContent,
  values,
  className,
  loading,
}: {
  setTooltipContent: any;
  values: number[];
  className?: string;
  loading?: boolean;
}) => {
  const { i18n } = useTranslation();
  const MAP_CODES = [1, 2, 3, 4];

  const mapValues = MAP_CODES
    .map((code) => values?.[code])
    .filter((v): v is number => typeof v === "number" && isFinite(v));

  const maxVal = mapValues.length ? Math.max(...mapValues) : 1;
  const minVal = mapValues.length ? Math.min(...mapValues) : -1;

  const colorScaleP = scaleQuantize<string>()
    .domain([0, maxVal || 1])
    .range([
      "#bbdee0",
      "#add9dc",
      "#9fd5d9",
      "#8fd1d6",
      "#80cdd4",
      "#70cad2",
      "#5fc7d1",
      "#4ec4d1",
      "#3cc1d0",
      "#2dbcce",
      "#26b1c3",
      "#20a5b8",
      "#1a98ab",
      "#158c9f",
      "#117f91",
      "#0d7284",
      "#096475",
      "#065766",
      "#044957",
      "#023b47",
    ]);

    const colorScaleN = scaleQuantize<string>()
    .domain([minVal || -1, 0])
    .range([
      "#393939",
      "#414141",
      "#4a4a4a",
      "#525252",
      "#5b5b5b",
      "#636363",
      "#6c6c6c",
      "#747474",
      "#7c7c7c",
      "#858585",
      "#8d8d8d",
      "#969696",
      "#9e9e9e",
      "#a6a6a6",
      "#afafaf",
      "#b7b7b7",
      "#c0c0c0",
      "#c8c8c8",
      "#d1d1d1",
      "#d9d9d9",
    ]);

  const getValue = (codigo: number): number => {
    const v = (values as any)?.[codigo];
    return typeof v === "number" ? v : 0;
  };

  return (
    <>
      {loading && (
        <div
          style={{
            justifyContent: "center",
            display: "flex",
            height: 400,
            marginBottom: -400,
          }}
        >
          <Spinner size={SpinnerSize.large} />
        </div>
      )}

<ComposableMap
        className={className ?? ""}
        data-tip=""
        width={800}
        height={500}
        projection="geoMercator"
        projectionConfig={{ scale: 4000, center: [-8.2, 53.4] }}
        style={{
          width: "100%",
          height: "auto",
          display: "block",
          transform: "scaleY(1.1)", 
          transformOrigin: "50% 50%",
        }}
      >
        <Geographies geography={geoUrl}>
          {({ geographies }) => (
            <>
              {geographies.map((geo: any) => {
                const codigo = Number(geo.properties.codigo);
                const val = getValue(codigo);
                const label =
                  i18n.language === "es"
                    ? geo.properties.ccaa || geo.properties.NAME_1 || geo.properties.name
                    : geo.properties.ccaaEn || geo.properties.name || geo.properties.NAME_1;
                return (
                  <Geography
                    key={codigo}
                    geography={geo}
                    fill={loading ? "#222222" : val > 0 ? colorScaleP(val) : colorScaleN(val)}
                    onMouseEnter={() => {
                      if (!loading) setTooltipContent(`${label}: ${val}%`);
                    }}
                    onMouseLeave={() => setTooltipContent("")}
                    style={{
                      default: {
                        outline: "none",
                        stroke: "#191A1B",
                        paintOrder: "stroke",
                        strokeWidth: 0.05,
                        fillOpacity: 0.9,
                      },
                      hover: {
                        fillOpacity: 1,
                        outline: "none",
                        stroke: "#191A1B",
                        paintOrder: "fill",
                        strokeWidth: 0,
                        cursor: "pointer",
                      },
                      pressed: { fillOpacity: 0.5, outline: "none" },
                    }}
                  />
                );
              })}

              {!loading &&
                geographies.map((geo: any) => {
                  const centroid = geoCentroid(geo) as [number, number];
                  return (
                    <g key={geo.properties.codigo + "-name"}>
                      <Marker coordinates={centroid}>
                        <text
                          fontSize={12}
                          pointerEvents="none"
                          paintOrder="stroke"
                          textAnchor="middle"
                          strokeWidth="2"
                          fill="white"
                          stroke="black"
                        >
                            {`${Math.round(getValue(geo.properties.codigo) * 10) / 10}%`}
                        </text>
                      </Marker>
                    </g>
                  );
                })}
            </>
          )}
        </Geographies>
      </ComposableMap>

      {!loading && values && (Array.isArray(values) ? values.length > 0 : Object.keys(values).length > 0) && (
        <div className="region-total">
          <IrelandMap fill={values[0] > 0 ? colorScaleP(values[0]) : colorScaleN(values[0])} />
          <span>
            <strong>Total:</strong> {values[0]}%
          </span>
        </div>
      )}
    </>
  );
};

export default memo(MapChartIRL);